import type { ReactNode } from "react";
import { MobileStatusMetric } from "../components/MobileStatusMetric.tsx";
import i18n from "../util/i18n.ts";
import { getPlayerRatingMetric, type PlayerRatingBand } from "./marketEvaluationMetrics.ts";

const OVERALL_BAND_DEFAULTS: Record<PlayerRatingBand, string> = {
  developing: "Developing",
  elite: "Elite level",
  limited: "Limited level",
  solid: "Solid level",
  strong: "Strong level",
};

const POTENTIAL_BAND_DEFAULTS: Record<PlayerRatingBand, string> = {
  developing: "Developing upside",
  elite: "Elite upside",
  limited: "Limited upside",
  solid: "Solid upside",
  strong: "Strong upside",
};

export const MarketRatingStatusMetric = ({
  className,
  hidden = false,
  kind = "overall",
  label,
  potential,
  rating,
}: {
  className?: string;
  hidden?: boolean;
  kind?: "overall" | "potential";
  label: ReactNode;
  potential?: number;
  rating: number;
}) => {
  const metric = getPlayerRatingMetric(rating, hidden);
  const hiddenLabel = i18n.t("marketMetrics.ratingHidden", {
    ns: "ui",
    defaultValue: "Rating hidden",
  });
  const value = metric.band
    ? i18n.t(`marketMetrics.${kind}Band.${metric.band}`, {
        ns: "ui",
        defaultValue:
          kind === "overall"
            ? OVERALL_BAND_DEFAULTS[metric.band]
            : POTENTIAL_BAND_DEFAULTS[metric.band],
      })
    : hiddenLabel;
  const detail = hidden
    ? undefined
    : potential === undefined
      ? i18n.t(
          kind === "overall"
            ? "marketMetrics.overallRatingDetail"
            : "marketMetrics.potentialRatingDetail",
          {
            ns: "ui",
            rating,
            defaultValue:
              kind === "overall" ? "Overall rating: {{rating}}" : "Potential rating: {{rating}}",
          },
        )
      : i18n.t("marketMetrics.combinedRatingDetail", {
          ns: "ui",
          ovr: rating,
          pot: potential,
          defaultValue: "Overall rating: {{ovr}} · Potential rating: {{pot}}",
        });

  return (
    <MobileStatusMetric
      className={className}
      detail={detail}
      label={label}
      score={metric.score}
      value={value}
    />
  );
};

export default MarketRatingStatusMetric;
